import React, { useState, useEffect } from "react";
import { formatKeyToUrl } from "./cloudApi";

const ImagePickerModal = ({
  isOpen,
  onClose,
  onSelect,
  userId,
  currentImageUrl,
}) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserImages();
    }
  }, [isOpen, userId]);

  const fetchUserImages = async () => {
    setLoading(true);
    try {
      const folderPath = `images/${userId}`;
      const response = await fetch(
        `https://api.uphub-app.workers.dev/files/folder/${encodeURIComponent(
          folderPath
        )}`
      );
      const data = await response.json();

      if (data.ok) {
        setImages(data.files);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      onSelect(file);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-tier-primary dark:bg-tier-primary-dark border dark:border-gray-600 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
        <h3 className="text-xl font-bold mb-4 text-color">Select an Image</h3>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-400">Loading images...</p>
          </div>
        ) : (
          <>
            <span className="block mb-2 text-sm text-color">
              Uploaded Images
            </span>
            <div className="flex-1 overflow-y-auto mb-4 border p-2">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                {images.map((img) => {
                  const fullUrl = formatKeyToUrl(img.key);
                  const isSelected = selectedImage === fullUrl;
                  const isCurrent =
                    currentImageUrl && currentImageUrl.includes(img.key);

                  return (
                    <div
                      key={img.key}
                      onClick={() => handleImageClick(fullUrl)}
                      className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${
                        isSelected
                          ? "border-blue-500 shadow-lg scale-105"
                          : isCurrent
                          ? "border-green-500"
                          : "border-gray-600 hover:border-gray-400"
                      }`}
                    >
                      <img
                        src={fullUrl}
                        alt={img.key}
                        className="w-full h-24 object-cover"
                      />
                      {isCurrent && (
                        <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1 rounded">
                          Current
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                          <span className="material-symbols-outlined text-white text-3xl">
                            check_circle
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {images.length === 0 && (
                <p className="text-center text-gray-400 py-8">
                  No images found. Upload a new one below.
                </p>
              )}
            </div>

            <div className="flex items-center w-full">
              <hr className="flex-grow border-t border-tier-secondary" />
              <span className="mx-4 text-alt-color">OR</span>
              <hr className="flex-grow border-t border-tier-secondary" />
            </div>

            <div className=" pt-4">
              <label className="block mb-2 text-sm text-color">
                Upload a new image:
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="w-full file-input mb-4"
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-action-white">
            Cancel
          </button>
          <button
            onClick={handleConfirmSelection}
            disabled={!selectedImage}
            className="btn-action disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Use Selected Image
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagePickerModal;
